import { useCallback } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import { useChatContext } from '@common/Chat/ChatContext'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useCrmIntegrationChatAvailability } from '@/hooks/useCrmIntegrationChatAvailability'
import {
  filterLeadProviderChatTabsByAccess,
  isLeadProviderConversationAccessible,
} from '@pages/Admin/CRM/utils/crmIntegrationAccess'
import {
  getLeadProviderChatTabs,
  getLeadVkUpdatedAt,
  getProviderUnreadCount,
} from '@pages/Admin/CRM/utils/leadFields'
import { getCrmIntegrationChatAccessDeniedAlert } from '@pages/Admin/CRM/components/integrations/common/lib/crmIntegrationChatAccessDeniedAlert'
import {
  useIntegrationChatDock,
  type IntegrationChatDockOpenOptions,
} from './integrationChatDockHooks'

export type OpenLeadIntegrationChatDockOptions = Omit<
  IntegrationChatDockOpenOptions,
  'initialUnreadCount' | 'leadVkUpdatedAt'
> & {
  conversationId?: number | null
  provider?: CrmChatProviderKey
}

export const useOpenLeadIntegrationChatDock = () => {
  const { openDock } = useIntegrationChatDock()
  const chatContext = useChatContext()
  const addAlert = useAddAlert()
  const { connectedIntegrations, isLoading: integrationsLoading } =
    useCrmIntegrationChatAvailability()

  return useCallback(
    (
      leadId: number,
      leadSource?: unknown,
      sessionOptions?: OpenLeadIntegrationChatDockOptions,
    ) => {
      chatContext?.changePage('default')
      const provider = sessionOptions?.provider ?? 'vkontakte'
      const allTabs = getLeadProviderChatTabs(leadSource, provider)
      const tabs =
        integrationsLoading || connectedIntegrations.length === 0
          ? allTabs
          : filterLeadProviderChatTabsByAccess(allTabs, connectedIntegrations)

      const requestedConversationId =
        sessionOptions?.conversationId != null &&
        Number.isFinite(Number(sessionOptions.conversationId))
          ? Number(sessionOptions.conversationId)
          : null

      if (
        !integrationsLoading &&
        connectedIntegrations.length > 0 &&
        requestedConversationId != null &&
        !isLeadProviderConversationAccessible(
          allTabs,
          connectedIntegrations,
          requestedConversationId,
        )
      ) {
        addAlert({
          type: 'error',
          title: 'Нет доступа',
          text: getCrmIntegrationChatAccessDeniedAlert(provider),
        })
        return
      }

      if (
        !integrationsLoading &&
        connectedIntegrations.length > 0 &&
        tabs.length === 0
      ) {
        addAlert({
          type: 'error',
          title: 'Нет доступа',
          text: getCrmIntegrationChatAccessDeniedAlert(provider),
        })
        return
      }

      const conversationId = (() => {
        if (
          requestedConversationId != null &&
          tabs.some(
            (tab) => Number(tab.conversation_id) === requestedConversationId,
          )
        ) {
          return requestedConversationId
        }

        const firstTab = tabs[0]
        return firstTab ? Number(firstTab.conversation_id) : null
      })()

      openDock(leadId, {
        initialUnreadCount: getProviderUnreadCount(leadSource, provider),
        leadVkUpdatedAt:
          provider === 'vkontakte'
            ? getLeadVkUpdatedAt(leadSource) ?? null
            : null,
        conversationId,
        communityTabs: sessionOptions?.communityTabs ?? tabs,
        provider,
        ...sessionOptions,
      })
    },
    [
      addAlert,
      chatContext,
      connectedIntegrations,
      integrationsLoading,
      openDock,
    ],
  )
}
